/**
 * SupportInbox — the admin Live Chat console. Two-pane layout: a list of
 * conversations on the left (filter by status + search, unread/online badges)
 * and the selected thread on the right with a reply composer. Polls so new
 * visitor messages surface without a manual refresh. Chats can be reopened,
 * closed (archived), or deleted; closed chats stay readable for management review.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import Icon from './Icon';

const LIST_POLL_MS = 8000;
const THREAD_POLL_MS = 5000;

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function SupportInbox() {
  const [chats, setChats] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);   // { chat, messages }
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const scrollerRef = useRef(null);

  // ── list ───────────────────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/support', {
        params: { status: statusFilter, search: search || undefined },
      });
      setChats(data.chats || []);
    } catch {
      // transient — keep showing the last good list
    } finally {
      setLoadingList(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    loadList();
    const id = setInterval(loadList, LIST_POLL_MS);
    return () => clearInterval(id);
  }, [loadList]);

  // ── selected thread ──────────────────────────────────────────────────────────
  const loadThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/admin/support/${id}`);
      setThread(data);
    } catch {
      toast.error('Could not load conversation');
    }
  }, []);

  useEffect(() => {
    if (!selectedId) { setThread(null); return undefined; }
    loadThread(selectedId);
    const id = setInterval(() => loadThread(selectedId), THREAD_POLL_MS);
    return () => clearInterval(id);
  }, [selectedId, loadThread]);

  // refresh the list shortly after opening a thread so its unread badge clears
  useEffect(() => { if (selectedId) loadList(); }, [thread?.messages?.length]); // eslint-disable-line

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [thread?.messages?.length]);

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || sending || !selectedId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/support/${selectedId}/reply`, { body: text });
      setThread(t => t ? { ...t, messages: [...t.messages, data] } : t);
      setReply('');
      loadList();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send reply');
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    if (!selectedId) return;
    try {
      await api.patch(`/admin/support/${selectedId}`, { status });
      setThread(t => t ? { ...t, chat: { ...t.chat, status } } : t);
      loadList();
    } catch {
      toast.error('Could not update status');
    }
  };

  const remove = async () => {
    if (!selectedId) return;
    if (!window.confirm('Delete this conversation and its history? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/support/${selectedId}`);
      setSelectedId(null); setThread(null);
      loadList();
      toast.success('Conversation deleted');
    } catch {
      toast.error('Could not delete conversation');
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }
  };

  const totalUnread = chats.reduce((n, c) => n + (c.unread || 0), 0);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
            <Icon name="chat" className="h-6 w-6 text-tea-600" />
            Live Chat
            {totalUnread > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-rose-600 text-white text-xs font-bold">{totalUnread} new</span>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage visitor conversations from the storefront chat widget.</p>
        </div>
        <div className="flex items-center gap-2">
          {['open', 'closed', 'all'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={'px-3 py-1.5 rounded-lg text-sm font-medium capitalize ' +
                (statusFilter === s ? 'bg-tea-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[20rem_1fr] gap-4 h-[calc(100vh-14rem)] min-h-[30rem]">
        {/* List */}
        <div className="rounded-xl border border-gray-200 bg-white flex flex-col overflow-hidden">
          <div className="p-2.5 border-b border-gray-100">
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email, topic…"
              className="w-full rounded-lg text-sm px-3 py-2 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-tea-400"
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingList ? (
              <div className="p-6 text-center text-sm text-gray-400">Loading…</div>
            ) : chats.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">No conversations.</div>
            ) : chats.map(c => (
              <button key={c.id} onClick={() => setSelectedId(c.id)}
                className={'w-full text-left px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors ' +
                  (selectedId === c.id ? 'bg-tea-50' : '')}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {c.online && <span className="inline-block w-2 h-2 rounded-full bg-green-500 shrink-0" title="Online" />}
                    <span className="font-semibold text-sm text-gray-800 truncate">{c.name}</span>
                  </div>
                  <span className="text-[11px] text-gray-400 shrink-0">{timeAgo(c.lastMessageAt || c.createdAt)}</span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="text-xs text-gray-500 truncate">
                    {c.lastSenderType === 'admin' ? 'You: ' : c.lastSenderType === 'system' ? '' : ''}
                    {c.lastSnippet || c.topic || '—'}
                  </span>
                  {c.unread > 0 && (
                    <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center">{c.unread}</span>
                  )}
                </div>
                {c.topic && <div className="text-[10px] text-tea-600 mt-0.5 truncate">{c.topic}</div>}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div className="rounded-xl border border-gray-200 bg-white flex flex-col overflow-hidden">
          {!thread ? (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
              Select a conversation to view it.
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-bold text-gray-900 flex items-center gap-2">
                    {thread.chat.online && <span className="inline-block w-2 h-2 rounded-full bg-green-500" title="Online now" />}
                    {thread.chat.name}
                    <span className={'text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ' +
                      (thread.chat.status === 'open' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>
                      {thread.chat.status}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3">
                    {thread.chat.email && <span>{thread.chat.email}</span>}
                    {thread.chat.topic && <span>Topic: {thread.chat.topic}</span>}
                    {thread.chat.pageUrl && <span>From: {thread.chat.pageUrl}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {thread.chat.status === 'open' ? (
                    <button onClick={() => setStatus('closed')} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200">Close</button>
                  ) : (
                    <button onClick={() => setStatus('open')} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-tea-600 text-white hover:bg-tea-700">Reopen</button>
                  )}
                  <button onClick={remove} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-rose-50 text-rose-600 hover:bg-rose-100">Delete</button>
                </div>
              </div>

              {/* Messages */}
              <div ref={scrollerRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
                {thread.messages.map(m => {
                  if (m.senderType === 'system') {
                    return (
                      <div key={m.id} className="mx-auto max-w-[90%] text-center text-xs text-gray-500 bg-white rounded-xl px-3 py-2 border border-gray-100">
                        {m.body}
                      </div>
                    );
                  }
                  const fromAdmin = m.senderType === 'admin';
                  return (
                    <div key={m.id} className={'flex ' + (fromAdmin ? 'justify-end' : 'justify-start')}>
                      <div className={'max-w-[80%] rounded-2xl px-3 py-2 ' +
                        (fromAdmin ? 'bg-tea-600 text-white rounded-br-sm' : 'bg-white text-gray-800 border border-gray-100 rounded-bl-sm')}>
                        <div className={'text-[10px] uppercase tracking-wide font-bold mb-0.5 ' + (fromAdmin ? 'text-white/80' : 'text-gray-400')}>
                          {fromAdmin ? 'You' : thread.chat.name}
                        </div>
                        <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                        <div className={'text-[9px] mt-1 ' + (fromAdmin ? 'text-white/70' : 'text-gray-400')}>
                          {new Date(m.createdAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Reply composer */}
              <div className="p-3 border-t border-gray-100">
                <div className="flex items-end gap-2">
                  <textarea
                    rows={2} value={reply} onChange={e => setReply(e.target.value)} onKeyDown={onKeyDown}
                    placeholder="Type your reply… (Enter to send, Shift+Enter for a new line)"
                    maxLength={4000}
                    className="flex-1 resize-none rounded-lg text-sm px-3 py-2 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-tea-400"
                  />
                  <button onClick={sendReply} disabled={!reply.trim() || sending}
                    className="shrink-0 px-4 py-2.5 rounded-lg text-sm font-bold text-white bg-tea-600 hover:bg-tea-700 disabled:opacity-40 disabled:cursor-not-allowed">
                    {sending ? '…' : 'Send'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
